import React, { useState, useEffect, useRef } from "react";
import isAccess from "../../utils/checkAccess.js";
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Snackbar,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import EditIcon from "../../assets/images/editIcon.svg";
import DeleteIcon from "../../assets/images/DeleteIcon.svg";
import AddEditDevice from "./AddEditDevice.js";
import AccessibleTooltip from "../Tooltip/Tooltip.js";
import DeviceDetailModal from "./DeviceDetailModal";
import DeleteModal from "./DeleteModal";
import "./style.scss";
import DeviceMangementFilter from "./DeviceMangementFilter";
import { useDispatch, useSelector } from "react-redux";
import Table from "../Table";
import {
  deleteDeviceComponents,
  fetAllDevices,
  fetchDeviceById,
  fetchCustomerDropdown,
  downloadActions,
} from "../../Actions/DeviceComponent.js";
import downarrow from "../../assets/images/downarrow.png";
import bluedownarrow from "../../assets/images/bluedownarrow.svg";
import { subscribeMQTT } from "../../Actions/mqttActions.js";
import SubHeader from "../SubHeader";
import "../../assets/styles/glButton.scss";

const DevicesComp = () => {
  const [open, setOpen] = useState(false);
  const [openToast, setOpenToast] = useState(false);

  // single anchors for menus (shared triggers can open the same menu)
  const [anchorElDownload, setAnchorElDownload] = useState(null);
  const [anchorElAdd, setAnchorElAdd] = useState(null);

  const lastOpenButtonRef = useRef(null); // remember which button opened the menu
  const firstInputRef = useRef(null); // optional initial focus

  const [hasData, setHasData] = useState(true);
  const [selectedEditId, setSelectedEditid] = useState();
  const [message, setMessage] = useState("Delete Successfully.");
  const [rows, setRows] = useState([]);
  const [formError, setFormError] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [actionButton, setActionButton] = useState("");
  const [editData, setEditData] = useState(null);
  const [deviceIdToDelete, setDeviceIdToDelete] = useState(null);
  const [filters, setFilters] = useState({
    customerNames: [],
    buildingId: [],
    floorName: [],
    deviceType: [],
  });

  // pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [openDetailDevice, setOpenDetailDevice] = useState(false);

  const dispatch = useDispatch();
  const {
    deviceListLoading,
    deviceListSuccess,
    deviceListError,
    deviceDeleteLoading,
    deviceDeleteSuccess,
    deviceDeleteError,
    deviceFiltersLoading,
    deviceFiltersSuccess,
    deviceDetailsLoading,
    deviceDetailsSuccess,
  } = useSelector((state) => state.deviceComponent);

  const token = JSON.parse(localStorage.getItem("token"));
  const decodedToken = jwtDecode(token);
  const { edit, delete: canDelete, create } = isAccess(
    decodedToken.userRole,
    "Device Management"
  );

  useEffect(() => {
    dispatch(fetchCustomerDropdown());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetAllDevices({
        customerNames: filters.customerNames,
        buildingId: filters.buildingId,
        floorName: filters.floorName,
        typeIds: filters.typeIds,
        page: page - 1,
        size: 5,
        ...(filters?.sort && {
          sortBy:
            filters.sort.field === "deviceType"
              ? "type"
              : filters.sort.field === "dateInstalled"
              ? "installed"
              : "",
          sortDirection: filters.sort.order,
        }),
      })
    );
  }, [filters, page, dispatch]);

  useEffect(() => {
    if (deviceListSuccess?.content) {
      setRows(deviceListSuccess.content);
      setTotalPages(deviceListSuccess.totalPages);
    }
  }, [deviceListLoading, deviceListSuccess, deviceListError]);

  useEffect(() => {
    if (!deviceDeleteLoading && deviceDetailsSuccess && actionButton === "edit") {
      setEditData(deviceDetailsSuccess);
      setOpen(true);
    }
  }, [deviceDetailsLoading, deviceDetailsSuccess, deviceDeleteLoading, actionButton, deviceListLoading]);

  useEffect(() => {
    if (deviceDeleteSuccess) {
      setOpenToast(true);
      dispatch(
        fetAllDevices({
          floorName: deviceFiltersSuccess?.floors
            ?.filter((floor) => floor.name === filters.floorName)
            .map((floorName) => floorName.name),
          typeIds: deviceFiltersSuccess?.deviceType
            ?.filter((deviceType) =>
              filters.deviceType.includes(deviceType.name)
            )
            .map((device) => device.id),
          page: page - 1,
          size: 5,
          ...(filters?.sort && {
            sortBy: filters.sort.field,
            sortDirection: filters.sort.order,
          }),
        })
      );
    }
  }, [deviceDeleteLoading, deviceDeleteSuccess, deviceDeleteError, dispatch, deviceFiltersSuccess, filters, page]);

  useEffect(() => {
    return () => dispatch({ type: "RESET_DEVICE_STATE" });
  }, [dispatch]);

  useEffect(() => {
    dispatch(subscribeMQTT(process.env.REACT_APP_DEVICES, () => {}));
  }, [dispatch]);

  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  const handleEdit = (selectedEditId) => {
    dispatch(fetchDeviceById(selectedEditId));
    setSelectedEditid(selectedEditId);
    setActionButton("edit");
  };

  const handleDelete = (id) => {
    dispatch(deleteDeviceComponents(id));
  };

  const handleToastClose = () => {
    setOpenToast(false);
    setMessage("");
  };

  // MENU open/close helpers (single Menu instance per type)
  const handleClickDownload = (event) => {
    lastOpenButtonRef.current = event.currentTarget;
    setAnchorElDownload(event.currentTarget);
  };
  const handleCloseDownload = () => {
    setAnchorElDownload(null);
    // restore focus to the button that opened the menu
    requestAnimationFrame(() => {
      lastOpenButtonRef.current?.focus();
    });
  };

  const handleClickAdd = (event) => {
    lastOpenButtonRef.current = event.currentTarget;
    setAnchorElAdd(event.currentTarget);
  };
  const handleCloseAdd = () => {
    setAnchorElAdd(null);
    requestAnimationFrame(() => {
      lastOpenButtonRef.current?.focus();
    });
  };

  const openDownload = Boolean(anchorElDownload);
  const openAdd = Boolean(anchorElAdd);

  const processedRows = Array.isArray(rows)
    ? rows.map((row) => ({
        id: row.id,
        customerName: row.customerName ?? "--",
        buildingId: row.buildingId ?? "--",
        serialNumber: row.serialNumber ?? "--",
        deviceName: row.deviceName ?? "--",
        deviceType: row.deviceType ?? "--",
        sourceType: row.sourceType ?? "--",
        deviceStatus: row.deviceStatus ?? "--",
      }))
    : [];

  const columns = [
    { field: "customerName", headerName: "Customer Name", flex: 1 },
    { field: "buildingId", headerName: "Building Id", flex: 1, sortable: true },
    { field: "serialNumber", headerName: "Serial Number", flex: 1 },
    { field: "deviceName", headerName: "Device Name", flex: 1, sortable: true },
    { field: "deviceType", headerName: "Device Type", flex: 1 },
    { field: "sourceType", headerName: "Source Type", flex: 1 },
    {
      field: "deviceStatus",
      headerName: "Device Status",
      flex: 1,
      renderCell: (params) => {
        return (
          <span
            className={
              params.row.deviceStatus === "in-use" ? "ACTIVE" : "INACTIVE"
            }
          >
            {params.row.deviceStatus === "in-use" ? "In-Use" : "Idle"}
          </span>
        );
      },
    },
    ...(edit || canDelete
      ? [
          {
            field: "Actions",
            headerName: "Actions",
            flex: 1,
            align: "right",
            headerAlign: "right",
            sortable: false,
            disableColumnMenu: true,
            renderCell: (row) => (
              <ul style={{ 
                display: "flex", 
                gap: "10px", 
                listStyle: "none",
                padding: 0,
                margin: 0
              }}>
                {edit && (
                  <li>
                    <AccessibleTooltip title="Edit">
                      <button
                        id={`edit-${row.row.id}`}
                        data-testid={`edit-${row.row.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(row.row.id);
                        }}
                        className="icon-btn edit-icon-btn"
                        aria-label="Edit"
                      >
                        <img
                          src={EditIcon}
                          alt=""
                          aria-hidden="true"
                          className="editIcon"
                        />
                      </button>
                    </AccessibleTooltip>
                  </li>
                )}
                {canDelete && (
                  <li>
                    <AccessibleTooltip title="Delete">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDeleteModal(true);
                          setDeviceIdToDelete(row.row.id);
                        }}
                        id={`delete-${row.row.id}`}
                        data-testid={`delete-${row.row.id}`}
                        className="icon-btn delete-icon-btn"
                        aria-label="Delete"
                      >
                        <img
                          src={DeleteIcon}
                          alt=""
                          aria-hidden="true"
                          className="deleteIcon"
                        />
                      </button>
                    </AccessibleTooltip>
                  </li>
                )}
              </ul>
            ),
          },
        ]
      : []),
  ];

    const handleDownload = (action) => {
    dispatch(downloadActions(action, {
        customerNames: filters.customerNames,
        buildingId: filters.buildingId,
        floorName: filters.floorName,
        typeIds: filters.typeIds,
        page: page - 1,
        size: 5,
        ...(filters?.sort && {
          sortBy:
            filters.sort.field === "deviceType"
              ? "type"
              : filters.sort.field === "dateInstalled"
              ? "installed"
              : "",
          sortDirection: filters.sort.order,
        }),
      }))
  };

  return (
    <>
      <span
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {!hasData && "No data available"}
        {deviceListError}
        {Object.values(formError).filter(Boolean).join(", ")}
      </span>

      <main
        role="main"
        className="main_ele_wrapper userMangement_wrapper"
        aria-label="Device Management Section"
      >
        <SubHeader
          heading="Device Management"
          headerButton={
            create && (
              <div className="new-entry downloadbulkBtns" role="list">
                <div role="listitem" className="mobilethreeDotsbtn">
                  <button
                    id="download-button-desktop"
                    ref={firstInputRef}
                    data-testid="download-button"
                    aria-controls={openDownload ? "download-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={openDownload ? "true" : undefined}
                    onClick={handleClickDownload}
                    className="threeDotsBtn outlinedButton"
                  >
                    Download
                    <span>
                      <img
                        src={bluedownarrow}
                        alt=""
                        aria-hidden="true"
                        className={` rotate ${openDownload ? "rotating" : ""}`}
                      />
                    </span>
                  </button>
                </div>

                <div role="listitem" style={{ marginLeft: "8px" }}>
                  <div className="mobilethreeDotsbtn">
                    <button
                      id="add-button-desktop"
                      data-testid="add-button"
                      aria-controls={openAdd ? "add-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={openAdd ? "true" : undefined}
                      onClick={handleClickAdd}
                      className="threeDotsBtn containedButton"
                    >
                      Add
                      <span>
                        <img
                          src={downarrow}
                          alt=""
                          aria-hidden="true"
                          className={` rotate ${openAdd ? "rotating" : ""}`}
                        />
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            )
          }
        />

        <DeviceMangementFilter
          filters={filters}
          setFilters={setFilters}
          page={page}
          setPage={setPage}
          onClear={() => {
            setFilters({ customerNames: [], buildingId: [], floorName: [], deviceType: [] });
            setPage(1);
          }}
          floorOptions={deviceFiltersSuccess?.floors?.map(
            (floor) => floor.name
          )}
          deviceTypeOption={deviceFiltersSuccess?.deviceType?.map(
            (floor) => floor.name
          )}
        />

        {/* Mobile action area — buttons only, they open the same menus */}
        <div className="new-EntryForMobile downloadBtnsForMobile">
          {create && (
            <div className="new-entry downloadbulkBtns">
              <div>
                <div className="mobilethreeDotsbtn">
                  <button
                    id="add-button-mobile"
                    data-testid="add-button"
                    aria-controls={openAdd ? "add-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={openAdd ? "true" : undefined}
                    onClick={handleClickAdd}
                    className="threeDotsBtn containedButton"
                  >
                    Add
                    <span>
                      <img
                        src={downarrow}
                        alt=""
                        aria-hidden="true"
                        className={` rotate ${openAdd ? "rotating" : ""}`}
                      />
                    </span>
                  </button>
                </div>
              </div>
              <div className="mobilethreeDotsbtn" style={{ marginLeft: "8px" }}>
                <button
                  id="download-button-mobile"
                  aria-controls={openDownload ? "download-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={openDownload ? "true" : undefined}
                  onClick={handleClickDownload}
                  className="threeDotsBtn outlinedButton"
                >
                  Download
                  <span>
                    <img
                      src={bluedownarrow}
                      alt=""
                      aria-hidden="true"
                      className={` rotate ${openDownload ? "rotating" : ""}`}
                    />
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="inner_Wrapper">
          <div className="floorMangementTable_wrapper deviceMangeTable_wrapper">
            {(deviceListLoading || deviceDeleteLoading || deviceFiltersLoading || deviceDetailsLoading) && processedRows.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
                data-testid="progressbar"
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ height: 400, width: "100%" }}>
                <Table
                  columns={columns}
                  rows={processedRows}
                  setHasData={setHasData}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  onRowClick={(row) => {
                    setOpenDetailDevice(true);
                    dispatch(fetchDeviceById(row.id));
                  }}
                  onSortChange={(value) => {
                    setFilters((filter) => ({
                      ...filter,
                      sort: value,
                    }));
                  }}
                />
              </Box>
            )}
          </div>
        </div>
      </main>

      {/* SINGLE Download Menu instance (opened by either desktop or mobile button) */}
      <Menu
        id="download-menu"
        anchorEl={anchorElDownload}
        open={openDownload}
        onClose={handleCloseDownload}
        MenuListProps={{
          'aria-labelledby': lastOpenButtonRef.current?.id || undefined,
          autoFocusItem: true,
          role: 'menu'
        }}
        onEntered={() => {
          // ensure first menuitem gets focus on open (robust fallback)
          const menuRoot = document.getElementById("download-menu");
          menuRoot?.querySelector('[role="menuitem"]')?.focus();
        }}
      >
        <MenuItem
          onClick={() => {
            handleDownload("deviceList");
            handleCloseDownload();
          }}
        >
          Device List
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleDownload("template");
            handleCloseDownload();
          }}
        >
          Template
        </MenuItem>
      </Menu>

      {/* SINGLE Add Menu instance */}
      <Menu
        id="add-menu"
        anchorEl={anchorElAdd}
        open={openAdd}
        onClose={handleCloseAdd}
        MenuListProps={{
          'aria-labelledby': lastOpenButtonRef.current?.id || undefined,
          autoFocusItem: true,
          role: 'menu'
        }}
        onEntered={() => {
          const menuRoot = document.getElementById("add-menu");
          setTimeout(() =>{
             menuRoot?.querySelector('[role="menuitem"]')?.focus();
          },200)
        }}
      >
        <MenuItem
          onClick={() => {
            setOpen(true);
            setActionButton("add");
            handleCloseAdd();
          }}
        >
          New Device
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpen(true);
            setActionButton("bulkupload");
            handleCloseAdd();
          }}
        >
          Bulk Upload
        </MenuItem>
      </Menu>

      <AddEditDevice
        open={open}
        setOpen={setOpen}
        setActionButton={setActionButton}
        actionButton={actionButton}
        editData={editData}
        formError={formError}
        setFormError={setFormError}
        existingDeviceNames={rows.length > 0 ? rows?.map((row) => row.name) : []}
        handleClose={() => {
          dispatch({ type: "RESTE_DEVICE_DETAILS" });
          dispatch({ type: "RESET_UPLOAD_LIST" });
          dispatch(
            fetAllDevices({
              floorName: deviceFiltersSuccess?.floors
                ?.filter((floor) => floor.name === filters.floorName)
                .map((floorName) => floorName.name),
              typeIds: deviceFiltersSuccess?.deviceType
                ?.filter((deviceType) => filters.deviceType.includes(deviceType.name))
                .map((device) => device.id),
              page: page - 1,
              size: 5,
              ...(filters?.sort && {
                sortBy:
                  filters.sort.field === "deviceType"
                    ? "type"
                    : filters.sort.field === "dateInstalled"
                    ? "installed"
                    : "",
                sortDirection: filters.sort.order,
              }),
            })
          );
          setActionButton("");
          setTimeout(() => {
            if (document.getElementById(selectedEditId)) {
              document.getElementById(selectedEditId).focus();
            }
          }, 200);
        }}
        rows={rows}
      />

      <DeleteModal
        setOpenDeleteModal={setOpenDeleteModal}
        openDeleteModal={openDeleteModal}
        handleDelete={() => handleDelete(deviceIdToDelete)}
      />

      <DeviceDetailModal
        open={openDetailDevice}
        onClose={() => setOpenDetailDevice(false)}
        device={deviceDetailsSuccess}
      />

      <Snackbar
        open={openToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        message={message}
        onClose={handleToastClose}
        className="success"
      />
    </>
  );
};

export default DevicesComp;
